import { OrderStatusProgressBar } from '../OrderStatusProgressBar';
import { getEstimatedDeliveryDate } from '../../utils/getEstimatedDeliveryDate';
import styles from './OrderStatusSummaryCard.module.css';
import ThemeProvider, { type ThemeProps, type ThemeType } from '@pixum/theme-provider';
import Text, { TEXT_VARIANT } from '@pixum/text';
import type { OrderSummaryNode } from 'data-layer';
import { getProductGroupsFromSubOrder, type OrderStatus, type ProductGroup } from '../../utils';
import Button, { ButtonVariants } from '@pixum/button';
import { RichTextBody } from '../../../richtText';
import clsx from 'clsx';
import type { SubOrderStatusType } from '../../utils/types';
import { OrderStatusDeliveryDetails } from './partials';
import { ORDER_STATUS } from '../../constants';

/**
 * Helper function to find the appropriate summary by status code
 * @param summaries - Array of order summary nodes
 * @param status - Status code to match
 * @returns Matching summary node or undefined
 */
function getSummaryByStatus(summaries: OrderSummaryNode[], status: string) {
  return summaries.find(summary => summary.type?.toUpperCase() === status.toUpperCase());
}

export interface OrderStatusSummaryCardProps {
  subOrder?: SubOrderStatusType;
  summaries: OrderSummaryNode[];
  productConstants: Record<string, string>;
  constants: Record<string, string>;
  theme: ThemeType;
  index: number;
  totalSubOrders: number;
  statusOverride?: OrderStatus;
  onContactClick?: () => void;
}

const calculateProductQuantitiesByTitle = (productGroups: ProductGroup[]) => {
  const groups: { [key: string]: number } = {};

  for (const productGroup of productGroups) {
    groups[productGroup.title] = (groups[productGroup.title] || 0) + productGroup.quantity;
  }

  return groups;
};

const getProductGroupSummaryText = (productGroups: ProductGroup[]) => {
  const groups = calculateProductQuantitiesByTitle(productGroups);

  return Object.keys(groups)
    .map(key => `${key} (${groups[key]}x)`)
    .join(', ');
};

/**
 * OrderStatusSummaryCard - Displays a single sub-order summary card
 *
 * This component handles the presentation of individual sub-order information including:
 * - Product group details
 * - Order status and progress
 * - Delivery dates and tracking information
 * - Status-specific content from CMS
 */
export function OrderStatusSummaryCard({
  subOrder,
  summaries,
  productConstants,
  constants,
  theme,
  index,
  totalSubOrders,
  statusOverride,
  onContactClick,
}: OrderStatusSummaryCardProps) {
  // Determine the status - use override if provided, otherwise get from subOrder
  const status: OrderStatus = statusOverride || (subOrder?.stateCode as OrderStatus);

  const productGroups = subOrder ? getProductGroupsFromSubOrder(subOrder, productConstants) : null;
  const productGroupSummaryText = productGroups && getProductGroupSummaryText(productGroups);

  const deliveryDate = subOrder
    ? getEstimatedDeliveryDate(
        subOrder.promisedMinDeliveryDate,
        subOrder.promisedMaxDeliveryDate,
        ' - ',
        subOrder.stateDate,
      )
    : null;

  const hasTrackingData = subOrder?.trackingNumber && subOrder?.trackingProviderName && subOrder?.trackingUrl;
  const deliveryInfo =
    deliveryDate && hasTrackingData
      ? `${constants['i18n_order-status_handed-to-post']} ${subOrder?.trackingProviderName}. ${constants['i18n_order-status_delivery-date']} ${deliveryDate} `
      : deliveryDate
        ? `${constants['i18n_order-status_delivery-date']} ${deliveryDate} `
        : null;

  const overline =
    totalSubOrders > 1
      ? `${constants['i18n_order-status_delivery']} ${index + 1}/${totalSubOrders}`
      : constants['i18n_order-status_ordered'];

  const summary = getSummaryByStatus(summaries, status) as OrderSummaryNode;
  const comments = summary?.description;

  const isOrderDelivered = status === ORDER_STATUS.DELIVERED;
  const isOrderUnknown = status === ORDER_STATUS.UNKNOWN;
  const isOrderShipped = status === ORDER_STATUS.SHIPPED;
  const isOrderSubmitted = status === ORDER_STATUS.SUBMITTED;

  const showProgressBar = !isOrderUnknown && !isOrderDelivered;
  const showTrackingUrl = isOrderShipped && subOrder?.trackingUrl;
  const showDeliveryInfo = deliveryDate && showProgressBar && !isOrderSubmitted;
  const showProductGroup = productGroupSummaryText && overline && !isOrderUnknown;

  const orderStatusText = <RichTextBody text={comments}></RichTextBody>;

  // The idea is to have a base ink and bright theme for the summary but inherit the rest from the parent theme
  const summaryTheme: ThemeType = {
    ...(theme as ThemeProps),
    base: 'ink',
    brightness: 'bright',
  };

  return (
    <ThemeProvider theme={summaryTheme}>
      <section
        className={clsx(
          styles.card,
          showProductGroup && styles.withProducts,
          showProgressBar && styles.withProgress,
          showDeliveryInfo && styles.withDelivery,
          showTrackingUrl && styles.withTracking,
        )}
        data-testid={`summary-${status}`}
      >
        {showProductGroup && (
          <div className={styles.productGroup}>
            <Text className={styles.overline} variant={TEXT_VARIANT.OVERLINE}>
              {overline}
            </Text>
            <Text variant={TEXT_VARIANT.SUBHEADLINE}>{productGroupSummaryText}</Text>
          </div>
        )}

        {(isOrderDelivered || isOrderUnknown) && summary?.image && (
          <img
            alt={summary.image.alt}
            className={styles.image}
            height={summary.image.height}
            src={summary.image.url}
            title={summary.image.title}
            width={summary.image.width}
          />
        )}

        {showProgressBar && <OrderStatusProgressBar status={status as string} constants={constants} />}

        <div className={styles.text}>{orderStatusText}</div>

        {showDeliveryInfo && (
          <OrderStatusDeliveryDetails
            deliveryInfo={deliveryInfo}
            showTrackingInfo={!!hasTrackingData}
            trackingUrl={subOrder?.trackingUrl}
            trackingNumber={subOrder?.trackingNumber}
            orderTrackingButtonText={summary?.buttonText}
          />
        )}

        {isOrderUnknown && summary?.buttonText && (
          <Button
            className={styles.button}
            label={summary.buttonText}
            variant={ButtonVariants.TEXT}
            onClick={onContactClick}
          >
            {summary.buttonText}
          </Button>
        )}
      </section>
    </ThemeProvider>
  );
}
